import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import { flushSync } from "react-dom";
import {
  applyOp, COMMON_SCALES, displaySize, feetPerInch, formatArea, formatLength, hitTest, lengthFeet, areaSquareFeet, markupCorners, paperSizeText,
  QUICK_AREA_UNIT_NAMES, QUICK_AREA_UNITS, QUICK_LENGTH_UNIT_NAMES, QUICK_LENGTH_UNITS, quantityText, scaleForIndex, scaleFromCalibration,
  scaleFromFeetPerInch, sheetLabelText, sheetNumberText, statusScaleText, UNSCALED_TEXT,
  type AppearanceColor, type CenterlineDocument, type Markup, type Measurement, type Operation, type QuickAreaUnit, type QuickLengthUnit, type Vec2,
  type PayItem, type SovItem, measurementChanges, type MeasurementUpdate,
} from "@centerline/core/viewer";
import { PinLayer, PinPanel, usePins } from "./PinLayer";
import { PhotoPinSheet, PhotoView, usePendingPhotoPins } from "./PhotoPin";
import { dropQueued } from "./offline";
import { PresenceDots } from "./ProjectPage";
import { PIN_TYPES, type Pin } from "@centerline/core/viewer";
import { MarkupCanvas } from "./MarkupCanvas";
import { Modal, type ModalSpec } from "./Modal";
import { canvasScale, freeCanvas, openPdf, sharpBudget, type PDFDocumentProxy, type PDFPageProxy, type PageViewport } from "./pdf";
import { sharpRegion, type SharpRegion } from "./sharp";
import { href } from "./router";
import { setMainRendering, Thumb } from "./Thumb";
import {
  COLOR_CHOICES, customStampDetails, DEFAULT_STAMP_SIZE, fittedTextBounds, minPoints, MULTI_POINT, newCustomStamp, nextLabel, presetDetails,
  quickMeasure, recolor, STAMP_PRESETS, stampMarkup, takeoff, textBox, TOOLS, type StampChoice, type ToolId,
  CLOSED, cloudMarkup, COMPLETED_LAYER, FREEHAND, highlightMarkup, inkMarkup, type InkStroke,
  isLine, LINE_WIDTHS, lineMarkup, snap45, resizedAboutCentre, SYMBOL_SHAPES, symbolMarkup, type SymbolShape,
} from "./tools";
import { actorName, setActorName, useDocSync } from "./useDocSync";
import { api } from "./api";
import { canApprove, useMe } from "./session";
import { sheetLabelFromInput } from "@centerline/core/viewer";
import { AttachSubmittal } from "./Submittals";
import { cachedPlan } from "./offline";

type Fit = "page" | "width" | null;
type Selection = { kind: "markup" | "measurement"; id: string } | null;
const ZOOM_STEPS = [0.05, 0.1, 0.15, 0.2, 0.25, 0.33, 0.5, 0.67, 0.75, 1, 1.25, 1.5, 2, 3, 4, 6, 8];
const MIN_ZOOM = 0.03, MAX_ZOOM = 8;
const KIND_LABEL: Record<string, string> = { pdf: "PDF", "centerline-project": ".centerline project", "centerline-pdf": "Centerline PDF" };
const MARKUP_LABEL: Record<Markup["kind"], string> = {
  note: "Text box", signature: "Signature", mot: "MOT symbol", workArea: "Work area", approvalStamp: "Stamp", image: "Image",
  highlight: "Highlight", cloud: "Revision cloud", shape: "Labeled shape", quickMeasure: "Quick measure",
};
const fmt1 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function useStored<T extends string>(key: string, allowed: readonly T[], fallback: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => { const s = localStorage.getItem(key); return (allowed as readonly string[]).includes(s ?? "") ? (s as T) : fallback; });
  return [v, (x) => { localStorage.setItem(key, x); setV(x); }];
}

const dist = (a: Vec2, b: Vec2) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const asPoint = ([x, y]: Vec2) => ({ x, y });

export function Viewer({ id, initialPage, focusPin, focusSel, place }: { id: string; initialPage: number; focusPin?: string; focusSel?: string; place?: string }) {
  const sync = useDocSync(id);
  const { doc, meta, apply } = sync;
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(initialPage - 1);
  const [zoom, setZoom] = useState(1);
  const [markupSharp, setMarkupSharp] = useState<(SharpRegion & { vpScale: number }) | null>(null);
  const [fit, setFit] = useState<Fit>("page");
  const [tool, setToolState] = useState<ToolId>("select");
  const [draft, setDraft] = useState<Vec2[]>([]);
  const [hover, setHover] = useState<Vec2 | null>(null);
  const [selection, setSelection] = useState<Selection>(null);
  const [moveDrag, setMoveDrag] = useState<{ start: Vec2; now: Vec2 } | null>(null);
  const [boxDrag, setBoxDrag] = useState<{ start: Vec2; now: Vec2 } | null>(null);
  /** Detail pin being placed: the callout spot on the source sheet, waiting for a box on the target sheet. */
  const [detailing, setDetailing] = useState<{ srcIndex: number; x: number; y: number; targetPageId: string; label: string } | null>(null);
  /** Showing a Detail pin's target: where to go back to. */
  const [detailView, setDetailView] = useState<{ pin: Pin; back: { pageIndex: number; zoom: number; fit: Fit; left: number; top: number } } | null>(null);
  const focusRef = useRef<{ pageId: string; rect: [number, number, number, number]; zoomed: boolean } | null>(null);
  const restoreRef = useRef<{ pageIndex: number; zoom: number; left: number; top: number } | null>(null);
  const [lengthUnit, setLengthUnit] = useStored<QuickLengthUnit>("centerline.quickLengthUnit", QUICK_LENGTH_UNITS, "ft");
  const [areaUnit, setAreaUnit] = useStored<QuickAreaUnit>("centerline.quickAreaUnit", QUICK_AREA_UNITS, "SF");
  const [sidebar, setSidebar] = useState(() => window.innerWidth >= 760);
  const [panel, setPanel] = useState<"none" | "info" | "layers" | "stamps">("none");
  const [visible, setVisible] = useState<string[] | null>(null);
  const [currentLayer, setCurrentLayer] = useState<string | null>(null);
  const [stamp, setStamp] = useState<StampChoice>({ kind: "preset", title: "APPROVED", color: "red" });
  const [modal, setModal] = useState<ModalSpec | null>(null);
  const [viewport, setViewport] = useState<PageViewport | null>(null);
  const [rendering, setRendering] = useState(false);
  const [resizeTick, setResizeTick] = useState(0);
  const [pageInput, setPageInput] = useState(String(initialPage));
  // Touch / Apple Pencil
  const [wheelMode, setWheelMode] = useStored("cl.wheelMode", ["zoom", "scroll"] as const, "zoom");
  const wheelModeRef = useRef(wheelMode); wheelModeRef.current = wheelMode;
  const [pencilOnly, setPencilOnly] = useState(() => localStorage.getItem("cl.pencilOnly") === "1");
  const [inkColor, setInkColor] = useState<AppearanceColor>("red");
  const [lineWidth, setLineWidthState] = useState<number>(() => { const v = Number(localStorage.getItem("cl.lineWidth")); return (LINE_WIDTHS as readonly number[]).includes(v) ? v : 2; });
  const [symbolShape, setSymbolShapeState] = useState<SymbolShape>(() => (localStorage.getItem("cl.symbol") as SymbolShape) || "triangle");
  const setSymbolShape = (v: SymbolShape) => { localStorage.setItem("cl.symbol", v); setSymbolShapeState(v); };
  const setLineWidth = (v: number) => { localStorage.setItem("cl.lineWidth", String(v)); setLineWidthState(v); };
  const [loupe, setLoupe] = useState<{ x: number; y: number; vx: number; vy: number; snapped: boolean } | null>(null);
  const [liveInk, setLiveInk] = useState<InkStroke | null>(null);
  const [movingPin, setMovingPin] = useState<string | null>(null);
  const [placingSub, setPlacingSub] = useState<string | null>(place ?? null);
  const [attachAt, setAttachAt] = useState<{ pageId: string; x: number; y: number } | null>(null);
  const me = useMe();
  const office = canApprove(me);
  const canRenameSheets = me?.role === "admin" || me?.role === "project_manager";
  useEffect(() => { if (place) setTool("pin"); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place]);
  const [selectedPinId, setSelectedPinId] = useState<string | null>(focusPin ?? null);
  const [sovItems, setSovItems] = useState<SovItem[]>([]);
  const [authors, setAuthors] = useState<Record<string, { by: string; at: string }>>({});
  const pointers = useRef(new Map<number, { type: string; x: number; y: number; sx: number; sy: number; t: number }>());
  const penUntil = useRef(0);
  const lastPointerType = useRef("mouse");
  const press = useRef<{ id: number; start: Vec2; type: string } | null>(null);
  const pinch = useRef<{ d0: number; z0: number; cx: number; cy: number; left: number; top: number } | null>(null);
  const lastTap = useRef<{ t: number; x: number; y: number } | null>(null);
  const inkRef = useRef<InkStroke | null>(null);

  const scroller = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTask = useRef<{ cancel(): void } | null>(null);
  const shownPage = useRef<PDFPageProxy | null>(null);
  const baseOut = useRef(1); // backing-store scale of the full-page image (below devicePixelRatio when capped)
  const sharpLayer = useRef<HTMLDivElement>(null);
  /** Sharp zoom: crisp re-render of just the visible area when the full-page image is capped. */
  const sharp = useRef<{ canvas: HTMLCanvasElement | null; region: SharpRegion | null; vpScale: number; task: { cancel(): void } | null; timer: number; seq: number; since: number }>({ canvas: null, region: null, vpScale: 0, task: null, timer: 0, seq: 0, since: 0 });
  const anchor = useRef<{ fx: number; fy: number; mx: number; my: number } | null>(null);
  const pan = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const activeThumb = useRef<HTMLButtonElement>(null);

  const pageCount = doc?.plan.pageCount ?? 0;
  const pageId = doc?.plan.pages[pageIndex]?.pageId ?? "";
  const layers = visible ?? doc?.layers.visible ?? [];
  const layer = currentLayer ?? doc?.layers.current ?? "Existing";

  useEffect(() => { if (doc && visible === null) setVisible(doc.layers.visible); }, [doc, visible]);
  useEffect(() => { if (doc && visible && doc.layers.available.some((l) => !doc.layers.visible.includes(l) && !visible.includes(l) && l === COMPLETED_LAYER)) setVisible([...visible, COMPLETED_LAYER]); }, [doc, visible]);
  const projectId = meta?.projectId;
  const pinView = useMemo(() => ({ documentId: id, pageId, sheet: doc && pageId ? sheetNumberText(doc, Math.max(0, doc.plan.pages.findIndex((p) => p.pageId === pageId))) : undefined }), [id, pageId, doc]);
  const pins = usePins(projectId, id, pinView);
  const pending = usePendingPhotoPins(id);
  const [photoAt, setPhotoAt] = useState<{ pageId: string; x: number; y: number; punch?: boolean } | null>(null);
  const [photoOpen, setPhotoOpen] = useState<string | null>(null);
  const [pinNote, setPinNote] = useState("");
  const allPins = useMemo(() => [...pins.pins, ...pending.asPins.filter((q) => !pins.pins.some((p) => p.id === q.id))], [pins.pins, pending.asPins]);
  const canEditPin = (p: { type: string; createdById?: string }) => (p.type !== "Photo" && p.type !== "Punch") || office || (!!me && p.createdById === me.id);
  const [pinFilter, setPinFilter] = useState<Set<string>>(() => new Set(PIN_TYPES));
  useEffect(() => { if (!focusPin) return; const p = pins.pins.find((x) => x.id === focusPin); if (p && doc) { const i = doc.plan.pages.findIndex((q) => q.pageId === p.pageId); if (i >= 0 && i !== pageIndex) setPageIndex(i); } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusPin, pins.pins.length, !!doc]);
  useEffect(() => { if (projectId) api.sov(projectId).then((r) => setSovItems(r.items)).catch(() => {}); }, [projectId]);
  useEffect(() => { const t = setTimeout(() => api.authors(id).then((r) => setAuthors(r.authors)).catch(() => {}), 400); return () => clearTimeout(t); }, [id, sync.seq]);
  useEffect(() => { if (!focusSel || !doc) return; if (doc.markups.some((m) => m.id === focusSel)) setSelection({ kind: "markup", id: focusSel }); else if (doc.measurements.some((m) => m.id === focusSel)) setSelection({ kind: "measurement", id: focusSel }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSel, !!doc]);

  // Open the plan with range requests once the document is known.
  useEffect(() => {
    if (!meta) return;
    // Saved for offline + no signal: read the stored copy. Online: range requests, falling back to the stored copy if the network fails.
    let cancelled = false, task: ReturnType<typeof openPdf> | null = null;
    const open = (data?: Uint8Array) => {
      task = openPdf(api.planUrl(id), data);
      const t = task;
      t.promise.then((p) => { if (!cancelled) setPdf(p); }).catch(async (e) => {
        if (cancelled) return;
        const saved = data ? null : await cachedPlan(id).catch(() => null);
        if (saved && !cancelled) open(saved); else if (!cancelled) setPdfError(navigator.onLine || data ? (e as Error).message : "This plan set isn't saved for offline. Open the project with signal and tap Save for offline.");
      });
    };
    if (navigator.onLine) open(); else cachedPlan(id).then((d) => { if (!cancelled) open(d ?? undefined); }).catch(() => open());
    return () => { cancelled = true; void task?.destroy(); };
  }, [id, meta]);

  useEffect(() => { if (pageCount && pageIndex > pageCount - 1) setPageIndex(pageCount - 1); }, [pageCount, pageIndex]);

  const setTool = useCallback((t: ToolId) => { setToolState(t); setDraft([]); setBoxDrag(null); if (t !== "select") setSelection(null); }, []);

  const goTo = useCallback((index: number) => {
    if (!pageCount) return;
    setPageIndex(Math.max(0, Math.min(pageCount - 1, index)));
    setDraft([]); setHover(null); setSelection(null);
  }, [pageCount]);

  useEffect(() => {
    history.replaceState(null, "", href({ name: "viewer", id, page: pageIndex + 1, ...(selectedPinId ? { pin: selectedPinId } : {}) }));
    setPageInput(String(pageIndex + 1));
  }, [id, pageIndex, selectedPinId]);
  useEffect(() => { activeThumb.current?.scrollIntoView({ block: "nearest" }); }, [pageIndex, pdf]);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    // Ignore small width/height changes (a desktop scrollbar showing/hiding is ~15px); otherwise fit -> scrollbar -> refit loops forever.
    let lastW = el.offsetWidth, lastH = el.offsetHeight;
    const ro = new ResizeObserver(() => {
      const w = el.offsetWidth, h = el.offsetHeight;
      if (Math.abs(w - lastW) < 24 && Math.abs(h - lastH) < 24) return;
      lastW = w; lastH = h; setResizeTick((t) => t + 1);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [pdf]);

  // Render the current page.
  useEffect(() => {
    if (!pdf) return;
    let cancelled = false;
    let offscreen: HTMLCanvasElement | null = null;
    (async () => {
      const page = await pdf.getPage(pageIndex + 1);
      if (cancelled) return;
      // Moving to another sheet: let pdf.js drop the previous sheet's parsed operators/fonts.
      if (shownPage.current && shownPage.current !== page) { clearSharp(); shownPage.current.cleanup(); }
      shownPage.current = page;
      const base = page.getViewport({ scale: 1 });
      let z = zoom;
      const el = scroller.current;
      if (fit && el) {
        // offsetWidth includes scrollbars, so the fit size doesn't change when one appears; 48px margin covers a scrollbar.
        const w = el.offsetWidth - 48, h = el.offsetHeight - 48;
        z = fit === "width" ? w / base.width : Math.min(w / base.width, h / base.height);
        z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
        if (Math.abs(z - zoom) > 1e-6) { setZoom(z); return; }
      }
      const vp = page.getViewport({ scale: z });
      const canvas = canvasRef.current;
      if (!canvas) return;
      const out = canvasScale(vp.width, vp.height); // capped on iPad/iPhone; above the cap the bitmap is stretched
      renderTask.current?.cancel(); // the last finished image stays on screen until this one is ready
      const off = document.createElement("canvas");
      offscreen = off;
      off.width = Math.max(1, Math.floor(vp.width * out));
      off.height = Math.max(1, Math.floor(vp.height * out));
      setRendering(true); setMainRendering(true);
      const task = page.render({ canvasContext: off.getContext("2d")!, viewport: vp, transform: out !== 1 ? [out, 0, 0, out, 0, 0] : undefined });
      renderTask.current = task;
      try { await task.promise; } catch (err) { if ((err as Error)?.name === "RenderingCancelledException") return; throw err; }
      if (renderTask.current === task) renderTask.current = null;
      if (cancelled) return;
      if (canvas.width !== off.width) canvas.width = off.width;
      if (canvas.height !== off.height) canvas.height = off.height;
      canvas.style.width = `${vp.width}px`; canvas.style.height = `${vp.height}px`;
      canvas.getContext("2d")!.drawImage(off, 0, 0);
      baseOut.current = out;
      freeCanvas(off); offscreen = null;
      flushSync(() => setViewport(vp)); // commit synchronously so the wheel-zoom preview is swapped for the real render in the same frame
      setRendering(false); setMainRendering(false);
    })().catch((e) => { setMainRendering(false); if (!cancelled) setPdfError(`Could not render page: ${(e as Error).message}`); });
    return () => {
      // A newer zoom/page supersedes this render: stop it now and release its bitmap (iOS counts canvas memory per tab).
      cancelled = true; renderTask.current?.cancel(); renderTask.current = null;
      if (offscreen) { freeCanvas(offscreen); offscreen = null; }
      setRendering(false); setMainRendering(false);
    };
  }, [pdf, pageIndex, zoom, fit, resizeTick]);

  function clearSharp() {
    const s = sharp.current;
    window.clearTimeout(s.timer); s.seq++;
    s.task?.cancel(); s.task = null;
    if (s.canvas) { s.canvas.remove(); freeCanvas(s.canvas); s.canvas = null; }
    s.region = null;
    if (sharpLayer.current) delete sharpLayer.current.dataset.sharpScale;
    setMarkupSharp(null);
  }
  function scheduleSharp() {
    const s = sharp.current;
    window.clearTimeout(s.timer);
    s.since = performance.now();
    s.timer = window.setTimeout(() => void renderSharp(), 150);
  }
  async function renderSharp() {
    const s = sharp.current, vp = viewportRef.current, page = shownPage.current, el = scroller.current, cv = canvasRef.current, layer = sharpLayer.current;
    if (!vp || !page || !el || !cv || !layer) return;
    if (wheelZoom.current || pinch.current || renderTask.current) { scheduleSharp(); return; } // gesture or full render still going
    const cr = cv.getBoundingClientRect(), sr = el.getBoundingClientRect();
    if (Math.abs(cr.width - vp.width) > 1) return; // CSS preview transform in place
    const dpr = window.devicePixelRatio || 1;
    const vis = { x: sr.left - cr.left, y: sr.top - cr.top, w: el.clientWidth, h: el.clientHeight };
    const reg = sharpRegion({ w: vp.width, h: vp.height }, vis, baseOut.current, dpr, sharpBudget());
    if (!reg) { clearSharp(); return; }
    const cur = s.region;
    if (cur && s.canvas && s.vpScale === vp.scale && Math.abs(cur.scale - reg.scale) < 1e-3
      && vis.x >= cur.x - 1 && vis.y >= cur.y - 1 && Math.min(vp.width, vis.x + vis.w) <= cur.x + cur.w + 1 && Math.min(vp.height, vis.y + vis.h) <= cur.y + cur.h + 1) return; // still covered
    s.task?.cancel(); s.task = null;
    const seq = ++s.seq;
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(reg.w * reg.scale)); c.height = Math.max(1, Math.round(reg.h * reg.scale));
    const task = page.render({ canvasContext: c.getContext("2d")!, viewport: vp, transform: [reg.scale, 0, 0, reg.scale, -reg.x * reg.scale, -reg.y * reg.scale] });
    s.task = task;
    try { await task.promise; } catch { freeCanvas(c); return; }
    if (seq !== s.seq || viewportRef.current !== vp) { freeCanvas(c); return; }
    s.task = null;
    c.className = "sharp-canvas";
    Object.assign(c.style, { left: `${reg.x}px`, top: `${reg.y}px`, width: `${reg.w}px`, height: `${reg.h}px` });
    layer.appendChild(c);
    if (s.canvas) { s.canvas.remove(); freeCanvas(s.canvas); }
    s.canvas = c; s.region = reg; s.vpScale = vp.scale;
    setMarkupSharp({ ...reg, vpScale: vp.scale });
    layer.dataset.sharpScale = String(reg.scale);
    layer.dataset.sharpMs = String(Math.round(performance.now() - s.since));
  }
  // New zoom/sheet/size: the old overlay is in the old coordinates, so drop it and render a fresh one once things settle.
  useEffect(() => { clearSharp(); if (viewport) scheduleSharp(); }, [viewport]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearSharp(), []); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    // Mouse-wheel zoom: drop the CSS preview, then keep the page point that was under the cursor under it.
    const g = wheelZoom.current, el0 = scroller.current, cv = canvasRef.current;
    if (g && el0 && cv && viewport) {
      const wrap = cv.parentElement as HTMLElement;
      wrap.style.transform = ""; wrap.style.transformOrigin = "";
      if (g.committed && Math.abs(viewport.scale - g.committed) < 1e-6) {
        const cr = cv.getBoundingClientRect();
        el0.scrollLeft += cr.left + g.u * cr.width - g.cx;
        el0.scrollTop += cr.top + g.v * cr.height - g.cy;
        if (g.done) wheelZoom.current = null;
        else { g.committed = undefined; applyWheelPreview(); }
      } else applyWheelPreview();
      anchor.current = null;
      return;
    }
    const a = anchor.current, el = scroller.current;
    if (!a || !el || !viewport) return;
    const inner = el.firstElementChild as HTMLElement;
    el.scrollLeft = a.fx * inner.scrollWidth - a.mx;
    el.scrollTop = a.fy * inner.scrollHeight - a.my;
    anchor.current = null;
  }, [viewport]);

  // Detail pins: zoom the target sheet to the detail box, or put the view back where it was.
  useLayoutEffect(() => {
    const el = scroller.current, cv = canvasRef.current;
    if (!el || !cv || !viewport || !doc) return;
    const centerOn = (vx: number, vy: number) => {
      const er = el.getBoundingClientRect(), cr = cv.getBoundingClientRect();
      el.scrollLeft += cr.left + vx - (er.left + el.clientWidth / 2);
      el.scrollTop += cr.top + vy - (er.top + el.clientHeight / 2);
    };
    const f = focusRef.current;
    if (f && doc.plan.pages[pageIndex]?.pageId === f.pageId) {
      const [x, y, w, h] = f.rect;
      const a = viewport.convertToViewportPoint(x, y) as [number, number], b = viewport.convertToViewportPoint(x + w, y + h) as [number, number];
      if (!f.zoomed) {
        f.zoomed = true;
        const bw = Math.abs(b[0] - a[0]) / viewport.scale, bh = Math.abs(b[1] - a[1]) / viewport.scale;
        const z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min((el.clientWidth - 48) / bw, (el.clientHeight - 48) / bh) * 0.85));
        if (Math.abs(z - viewport.scale) > 1e-3) { setZoom(z); return; }
      }
      centerOn((a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
      focusRef.current = null;
      return;
    }
    const r = restoreRef.current;
    if (r && r.pageIndex === pageIndex && Math.abs(viewport.scale - r.zoom) < 1e-3) {
      el.scrollLeft = r.left; el.scrollTop = r.top;
      restoreRef.current = null;
    }
  }, [viewport]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = useCallback((pin: Pin) => {
    if (!doc || !pin.detail) return;
    const target = doc.plan.pages.findIndex((p) => p.pageId === pin.detail!.pageId);
    if (target < 0) return;
    const el = scroller.current;
    setDetailView((cur) => ({ pin, back: cur?.back ?? { pageIndex, zoom, fit, left: el?.scrollLeft ?? 0, top: el?.scrollTop ?? 0 } }));
    setSelectedPinId(null); setSelection(null);
    focusRef.current = { pageId: pin.detail.pageId, rect: pin.detail.rect, zoomed: false };
    setFit(null);
    if (target === pageIndex) setResizeTick((t) => t + 1); else setPageIndex(target);
  }, [doc, pageIndex, zoom, fit]);

  const closeDetail = useCallback((reselect: boolean) => {
    if (!detailView) return;
    const { back, pin } = detailView;
    setDetailView(null);
    focusRef.current = null;
    restoreRef.current = { pageIndex: back.pageIndex, zoom: back.zoom, left: back.left, top: back.top };
    setFit(back.fit); setZoom(back.zoom); setPageIndex(back.pageIndex);
    setResizeTick((t) => t + 1);
    if (reselect) setSelectedPinId(pin.id);
  }, [detailView]);

  const setZoomAround = useCallback((next: number, clientX?: number, clientY?: number) => {
    const el = scroller.current;
    const z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next));
    if (el) {
      const rect = el.getBoundingClientRect();
      const mx = (clientX ?? rect.left + rect.width / 2) - rect.left;
      const my = (clientY ?? rect.top + rect.height / 2) - rect.top;
      const inner = el.firstElementChild as HTMLElement;
      anchor.current = { fx: (el.scrollLeft + mx) / Math.max(1, inner.scrollWidth), fy: (el.scrollTop + my) / Math.max(1, inner.scrollHeight), mx, my };
    }
    setFit(null);
    setZoom(z);
  }, []);
  const zoomIn = useCallback(() => setZoomAround(ZOOM_STEPS.find((s) => s > zoom + 1e-6) ?? MAX_ZOOM), [zoom, setZoomAround]);
  const zoomOut = useCallback(() => setZoomAround([...ZOOM_STEPS].reverse().find((s) => s < zoom - 1e-6) ?? MIN_ZOOM), [zoom, setZoomAround]);

  const scale = doc ? scaleForIndex(doc, pageIndex) : undefined;
  const fpp = scale?.feetPerPoint;
  const tol = 6 / (viewport?.scale ?? 1);

  const selectedMarkup = selection?.kind === "markup" ? doc?.markups.find((m) => m.id === selection.id) : undefined;
  const selectedMeasurement = selection?.kind === "measurement" ? doc?.measurements.find((m) => m.id === selection.id) : undefined;
  useEffect(() => { if (selection && !selectedMarkup && !selectedMeasurement) setSelection(null); }, [selection, selectedMarkup, selectedMeasurement]);
  // Selecting something shows its inspector (with Rename); the Info panel would otherwise hide it.
  useEffect(() => { if (selection) setPanel((p) => (p === "info" ? "none" : p)); }, [selection]);

  const deleteSelection = useCallback(() => {
    if (!selection) return;
    if (apply(selection.kind === "markup" ? { type: "deleteMarkup", id: selection.id } : { type: "deleteMeasurement", id: selection.id })) setSelection(null);
  }, [selection, apply]);

  // Pay items: pick a schedule-of-values line, or type one.
  const payItemFields = (cur?: PayItem) => [
    { name: "sov", label: "Pay item (from SOV)", type: "select" as const, value: cur ? (sovItems.find((i) => i.itemNo === cur.number)?.id ?? "") : "",
      options: [{ value: "", label: sovItems.length ? "— none / typed below —" : "— no SOV items yet —" }, ...sovItems.map((i) => ({ value: i.id, label: `${i.itemNo ? `${i.itemNo} · ` : ""}${i.description} (${i.unit})` }))] },
    { name: "payNo", label: "…or pay item no.", value: cur && !sovItems.some((i) => i.itemNo === cur.number) ? cur.number : "", placeholder: "e.g. 0334 1 53" },
    { name: "payName", label: "Pay item description", value: cur && !sovItems.some((i) => i.itemNo === cur.number) ? cur.name : "" },
    { name: "payUnit", label: "Pay unit", value: cur && !sovItems.some((i) => i.itemNo === cur.number) ? cur.unit : "", placeholder: "SY, TN, LF…" },
  ];
  const payItemFrom = (v: Record<string, unknown>): PayItem | undefined => {
    const it = sovItems.find((i) => i.id === v.sov);
    if (it) return { number: it.itemNo || it.description.slice(0, 20), name: it.description, unit: it.unit };
    const no = String(v.payNo ?? "").trim();
    return no ? { number: no, name: String(v.payName ?? "").trim(), unit: String(v.payUnit ?? "").trim().toUpperCase() } : undefined;
  };

  // MARK: finishing multi-point tools
  const finishDraft = useCallback((pts: Vec2[]) => {
    if (!doc) return;
    const cleaned = pts.filter((p, i) => i === 0 || dist(p, pts[i - 1]!) > tol / 2);
    if (cleaned.length < minPoints(tool)) { setDraft([]); return; }
    setDraft([]);
    switch (tool) {
      case "measureDistance": apply({ type: "addMarkup", markup: quickMeasure(pageId, layer, cleaned, false, lengthUnit, areaUnit) }); break;
      case "measureArea": apply({ type: "addMarkup", markup: quickMeasure(pageId, layer, cleaned, true, lengthUnit, areaUnit) }); break;
      case "cloud": apply({ type: "addMarkup", markup: cloudMarkup(pageId, layer, cleaned, inkColor) }); break;
      case "line": { const m = lineMarkup(pageId, layer, cleaned[0]!, cleaned[cleaned.length - 1]!, inkColor, lineWidth, lengthUnit); apply({ type: "addMarkup", markup: m }); break; }
      case "arrow": apply({ type: "addMeasurement", measurement: { ...takeoff(doc, pageId, "arrow", cleaned.slice(-2), ""), layer, color: inkColor } }); break;
      case "linear": case "area": case "count": {
        const kind = tool;
        setModal({
          title: kind === "linear" ? "New linear (centerline) takeoff" : kind === "area" ? "New area takeoff" : "New count",
          message: `On layer “${layer}”. ${fpp ? "" : "This sheet has no scale yet — the quantity shows once it is calibrated."}`,
          fields: [{ name: "label", label: "Label", value: nextLabel(doc, kind) }, ...payItemFields()],
          submit: "Add takeoff",
          onSubmit: (v) => { const payItem = payItemFrom(v); apply({ type: "addMeasurement", measurement: { ...takeoff(doc, pageId, kind, cleaned, String(v.label) || payItem?.name || nextLabel(doc, kind)), layer, ...(payItem ? { payItem } : {}) } }); },
        });
        break;
      }
      case "calibrate": break;
    }
  }, [doc, tool, pageId, layer, lengthUnit, areaUnit, apply, tol, fpp, inkColor, sovItems, lineWidth]);

  const openCalibrate = useCallback((a: Vec2, b: Vec2) => {
    const raw = dist(a, b);
    setModal({
      title: "Calibrate sheet",
      message: <>The line you drew is {fmt1.format(raw)} pt ({(raw / 72).toFixed(2)} in on the sheet). Enter its real length.</>,
      fields: [{ name: "feet", label: "Known distance (feet)", type: "number", placeholder: "e.g. 50" }],
      submit: "Set scale",
      onSubmit: (v) => {
        const feet = Number(String(v.feet).replace(/[^0-9.]/g, ""));
        const s = scaleFromCalibration(asPoint(a), asPoint(b), feet);
        if (!s) return "Enter a distance greater than zero.";
        apply({ type: "setPageScale", pageId, scale: s });
        setTool("select");
      },
    });
  }, [apply, pageId, setTool]);

  const renameSheet = useCallback(() => {
    if (!doc || !pageId) return;
    const cur = doc.sheetLabels[pageId];
    setModal({
      title: "Rename sheet",
      message: <>Sheet {pageIndex + 1} of {pageCount}. Leave the number blank to go back to “Sheet {pageIndex + 1}”.</>,
      fields: [
        { name: "number", label: "Sheet number", value: cur?.number ?? "", placeholder: "e.g. C4.12" },
        { name: "title", label: "Sheet title (optional)", value: cur?.title ?? "", placeholder: "e.g. Utility Plan" },
      ],
      submit: "Save",
      onSubmit: (v) => { apply({ type: "setSheetLabel", pageId, label: sheetLabelFromInput(String(v.number ?? ""), String(v.title ?? "").trim() || undefined) }); },
    });
  }, [doc, pageId, pageIndex, pageCount, apply]);

  const openScaleMenu = useCallback(() => {
    setModal({
      title: "Set sheet scale",
      message: <>Sheet {doc ? sheetNumberText(doc, pageIndex) : ""} is {scale ? scale.displayLabel : "unscaled"}. Pick a common scale, type feet per inch, or use 📐 Calibrate.</>,
      fields: [
        { name: "preset", label: "Common scales", type: "select", value: "", options: [{ value: "", label: "—" }, ...COMMON_SCALES.map((s) => ({ value: String(s.feetPerInch), label: s.label })), { value: "clear", label: "Remove scale" }] },
        { name: "fpi", label: "…or feet per inch", type: "number", placeholder: scale ? String(+feetPerInch(scale).toFixed(4)) : "e.g. 20" },
      ],
      submit: "Apply",
      onSubmit: (v) => {
        if (v.preset === "clear") { apply({ type: "setPageScale", pageId, scale: null }); return; }
        const fpi = Number(v.fpi || v.preset);
        const s = scaleFromFeetPerInch(fpi, undefined, v.fpi ? "direct" : "preset");
        if (!s) return "Pick a scale or enter feet per inch.";
        apply({ type: "setPageScale", pageId, scale: s });
      },
    });
  }, [doc, pageIndex, scale, apply, pageId]);

  const placeText = useCallback((rect: [number, number, number, number] | null, at: Vec2) => {
    setModal({
      title: "Text box", fields: [{ name: "text", label: "Text", type: "textarea", placeholder: "Type the note…" }], submit: "Place",
      message: "⌘↩ places it. Colour and size can be changed after.",
      onSubmit: (v) => {
        const text = String(v.text).trim();
        if (!text) return "Type some text.";
        const m = textBox(pageId, layer, fittedTextBounds(text, 11, at, rect ?? undefined), text);
        if (apply({ type: "addMarkup", markup: m })) setSelection({ kind: "markup", id: m.id });
      },
    });
  }, [pageId, layer, apply]);

  const placeStamp = useCallback((rect: [number, number, number, number] | null, at: Vec2) => {
    const size = stamp.kind === "custom" ? stamp.stamp.size : DEFAULT_STAMP_SIZE;
    const bounds: [number, number, number, number] = rect ?? [at[0] - size[0] / 2, at[1] - size[1] / 2, size[0], size[1]];
    if (stamp.kind === "custom") {
      const m = stampMarkup(pageId, layer, bounds, customStampDetails(stamp.stamp, actorName()));
      if (apply({ type: "addMarkup", markup: m })) setSelection({ kind: "markup", id: m.id });
      return;
    }
    const preset = stamp;
    setModal({
      title: `Place ${preset.title} stamp`, message: "Optional name, date, and project are drawn in the stamp.", submit: "Place stamp",
      fields: [
        { name: "name", label: "Name", value: actorName() === "Web user" ? "" : actorName(), placeholder: "Optional approver" },
        { name: "date", label: "Date", value: new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(new Date()), placeholder: "Optional date" },
        { name: "project", label: "Project", placeholder: "Optional project" },
      ],
      onSubmit: (v) => {
        const m = stampMarkup(pageId, layer, bounds, presetDetails(preset.title, preset.color, { name: String(v.name), date: String(v.date), project: String(v.project) }));
        if (apply({ type: "addMarkup", markup: m })) setSelection({ kind: "markup", id: m.id });
      },
    });
  }, [stamp, pageId, layer, apply]);

  const openNewStamp = useCallback(() => {
    setModal({
      title: "New custom stamp", message: "Saved with this plan set, so everyone working on it (web and Mac) can place it.", submit: "Save stamp",
      fields: [
        { name: "title", label: "Stamp text", value: "REVIEWED" },
        { name: "project", label: "Second line", placeholder: "e.g. company or project" },
        { name: "name", label: "Name in the stamp list", placeholder: "defaults to the stamp text" },
        { name: "color", label: "Colour", type: "select", value: "navy", options: COLOR_CHOICES.map((c) => ({ value: c, label: c })) },
        { name: "shape", label: "Shape", type: "select", value: "roundedRectangle", options: [
          { value: "rectangle", label: "Rectangle" }, { value: "roundedRectangle", label: "Rounded" }, { value: "doubleBorder", label: "Double border" }, { value: "oval", label: "Oval / seal" }] },
        { name: "showDate", label: "Add today's date when placed", type: "checkbox", value: true },
        { name: "showUser", label: "Add my name when placed", type: "checkbox", value: true },
      ],
      onSubmit: (v) => {
        if (!String(v.title).trim()) return "Stamp text is required.";
        const def = newCustomStamp({ name: String(v.name), title: String(v.title), project: String(v.project), color: v.color as AppearanceColor, shape: v.shape as "oval", showDate: Boolean(v.showDate), showUser: Boolean(v.showUser), userName: actorName() === "Web user" ? "" : actorName() });
        if (apply({ type: "upsertStampDefinition", stamp: def })) { setStamp({ kind: "custom", stamp: def }); setTool("stamp"); }
      },
    });
  }, [apply, setTool]);

  /** Rename a takeoff and/or change its pay item. Goes through the normal op path (undo/redo, offline queue, live sync). */
  const updateMeasurement = useCallback((id: string, set: MeasurementUpdate) => apply({ type: "patchMeasurement", id, set }), [apply]);
  const renameMeasurement = useCallback((m: Measurement) => {
    setModal({
      title: "Rename takeoff",
      fields: [{ name: "label", label: "Label", value: m.label }, ...payItemFields(m.payItem), { name: "notes", label: "Notes", type: "textarea", value: m.notes ?? "" }],
      submit: "Save",
      onSubmit: (v) => { const set = measurementChanges(m, { label: String(v.label ?? ""), payItem: payItemFrom(v) ?? null, notes: String(v.notes ?? "") || null }); if (Object.keys(set).length) updateMeasurement(m.id, set); },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMeasurement, sovItems]);

  const editSelected = useCallback(() => {
    if (selectedMarkup?.kind === "note") {
      const m = selectedMarkup;
      setModal({
        title: "Edit text", fields: [{ name: "text", label: "Text", type: "textarea", value: m.style.text }], submit: "Save",
        onSubmit: (v) => { apply({ type: "patchMarkup", id: m.id, set: { style: { ...m.style, text: String(v.text) } } }); },
      });
    } else if (selectedMeasurement) {
      renameMeasurement(selectedMeasurement);
    }
  }, [selectedMarkup, selectedMeasurement, apply, sovItems, renameMeasurement]);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (modal) return;
      const t = e.target as HTMLElement;
      if (t && (t.tagName === "INPUT" || t.tagName === "SELECT" || t.tagName === "TEXTAREA")) return;
      if (e.metaKey || e.ctrlKey) {
        if (e.key === "z" || e.key === "Z") { e.preventDefault(); if (e.shiftKey) sync.redo(); else sync.undo(); }
        else if (e.key === "y") { e.preventDefault(); sync.redo(); }
        else if (e.key === "=" || e.key === "+") { e.preventDefault(); zoomIn(); }
        else if (e.key === "-") { e.preventDefault(); zoomOut(); }
        else if (e.key === "0") { e.preventDefault(); setFit("page"); }
        return;
      }
      if (e.key === "Enter" && MULTI_POINT.has(tool)) { e.preventDefault(); finishDraft(draft); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && selection) { e.preventDefault(); deleteSelection(); return; }
      if (e.key === "Escape") { cancelTool(); return; }
      const toolKey = TOOLS.find((x) => x.key === e.key.toLowerCase());
      if (toolKey && !e.shiftKey && !e.altKey && (toolKey.id !== "detailpin" || office)) { setTool(toolKey.id); return; }
      switch (e.key) {
        case "ArrowRight": case "PageDown": case "j": e.preventDefault(); goTo(pageIndex + 1); break;
        case "ArrowLeft": case "PageUp": e.preventDefault(); goTo(pageIndex - 1); break;
        case "Home": e.preventDefault(); goTo(0); break;
        case "End": e.preventDefault(); goTo(pageCount - 1); break;
        case "+": case "=": zoomIn(); break;
        case "-": case "_": zoomOut(); break;
        case "0": setFit("page"); break;
        case "w": case "W": setFit("width"); break;
        case "1": setZoomAround(1); break;
        case "g": { e.preventDefault(); (document.getElementById("page-jump") as HTMLInputElement | null)?.select(); break; }
        case "[": setSidebar((s) => !s); break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modal, tool, draft, selection, finishDraft, deleteSelection, goTo, pageIndex, pageCount, zoomIn, zoomOut, setZoomAround, setTool, sync, office, detailing]);

  const pagePoint = (e: { clientX: number; clientY: number }): Vec2 | null => {
    if (!viewport || !canvasRef.current) return null;
    const rect = canvasRef.current.getBoundingClientRect();
    const [x, y] = viewport.convertToPdfPoint(e.clientX - rect.left, e.clientY - rect.top) as [number, number];
    return [x, y];
  };

  // Escape and right-click: drop any in-progress markup and go back to the navigate tool.
  const cancelTool = () => { if (detailing) setDetailing(null); setDraft([]); setBoxDrag(null); boxRef.current = null; inkRef.current = null; setLiveInk(null); setMoveDrag(null); setSelection(null); setPanel("none"); if (tool !== "select") setTool("select"); };
  const onContextMenu = (e: ReactMouseEvent) => { e.preventDefault(); if (tool !== "select" || draft.length || detailing || boxDrag || inkRef.current) cancelTool(); };
  const mouseDown = (e: ReactMouseEvent) => {
    if (e.button === 1) { const el = scroller.current!; pan.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop }; e.preventDefault(); return; } // middle-drag pans
    if (e.button !== 0 || !doc) return;
    const p = pagePoint(e);
    const startPan = () => { const el = scroller.current!; pan.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop }; e.preventDefault(); };
    if (tool === "pan" || e.altKey || !p) return startPan();
    if (tool === "select") {
      const hit = hitTest(doc, pageId, p, tol, layers);
      setSelection(hit);
      if (hit) { setMoveDrag({ start: p, now: p }); e.preventDefault(); } else startPan();
      return;
    }
    if (tool === "text" || tool === "stamp" || tool === "highlight" || tool === "detailbox") { setBoxDrag({ start: p, now: p }); e.preventDefault(); }
    if (FREEHAND.has(tool)) { inkRef.current = { pts: [p], pressure: [0.5] }; setLiveInk(inkRef.current); e.preventDefault(); }
  };
  const mouseMove = (e: ReactMouseEvent) => {
    if (pan.current) {
      const el = scroller.current!;
      el.scrollLeft = pan.current.left - (e.clientX - pan.current.x);
      el.scrollTop = pan.current.top - (e.clientY - pan.current.y);
    }
    const raw = pagePoint(e);
    const p = raw && tool === "line" && e.shiftKey && draft.length === 1 ? snap45(draft[0]!, raw) : raw;
    setHover(p);
    if (p && moveDrag) setMoveDrag({ ...moveDrag, now: p });
    if (p && boxDrag) setBoxDrag({ ...boxDrag, now: p });
    if (p && inkRef.current && e.buttons) { inkRef.current = { pts: [...inkRef.current.pts, p], pressure: [...inkRef.current.pressure, 0.5] }; setLiveInk(inkRef.current); }
  };
  const mouseUp = () => {
    pan.current = null;
    if (moveDrag && selection) {
      const dx = moveDrag.now[0] - moveDrag.start[0], dy = moveDrag.now[1] - moveDrag.start[1];
      if (Math.hypot(dx, dy) > tol / 3) apply(selection.kind === "markup" ? { type: "moveMarkup", id: selection.id, dx, dy } : { type: "moveMeasurement", id: selection.id, dx, dy });
    }
    setMoveDrag(null);
    if (boxDrag) {
      const [x0, y0] = boxDrag.start, [x1, y1] = boxDrag.now;
      const rect: [number, number, number, number] | null = Math.abs(x1 - x0) > 8 / (viewport?.scale ?? 1) && Math.abs(y1 - y0) > 8 / (viewport?.scale ?? 1)
        ? [Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0)] : null;
      setBoxDrag(null);
      if (tool === "text") placeText(rect, boxDrag.start);
      else if (tool === "stamp") placeStamp(rect, boxDrag.start);
      else if (tool === "highlight" && rect) placeHighlight(rect);
      else if (tool === "detailbox" && rect) placeDetailBox(rect);
    }
    if (inkRef.current) finishInk();
  };
  const onClick = (e: ReactMouseEvent) => {
    if (lastPointerType.current !== "mouse") return; // pen/touch taps are handled on pointerup
    if (tool === "pin" || tool === "photopin" || tool === "punchpin" || tool === "subpin" || tool === "detailpin") { const p = pagePoint(e); if (p) dropPin(p); return; }
    if (tool === "symbol") { const p = pagePoint(e); if (p) placeSymbol(p); return; }
    if (e.altKey || !(MULTI_POINT.has(tool) || tool === "calibrate")) return;
    const p = pagePoint(e);
    if (!p) return;
    if (e.detail >= 2) return; // the dblclick handler finishes
    if (tool === "calibrate") {
      if (draft.length === 0) setDraft([p]);
      else { setDraft([]); openCalibrate(draft[0]!, p); }
      return;
    }
    if (tool === "line") {
      if (draft.length === 0) setDraft([p]);
      else finishDraft([draft[0]!, e.shiftKey ? snap45(draft[0]!, p) : p]);
      return;
    }
    setDraft((d) => [...d, p]);
  };
  const onDoubleClick = (e: ReactMouseEvent) => {
    if (lastPointerType.current !== "mouse") return;
    if (tool === "select") {
      const p = pagePoint(e); const hit = p && doc ? hitTest(doc, pageId, p, tol, layers) : null;
      if (hit && hit.kind === "measurement") { const m = doc!.measurements.find((x) => x.id === hit.id); if (m) { setSelection({ kind: "measurement", id: m.id }); renameMeasurement(m); } }
      else if (hit) editSelected();
      return;
    }
    if (!MULTI_POINT.has(tool) || tool === "count") return;
    finishDraft(draft);
  };

  // MARK: touch, Apple Pencil, pinch
  const placeHighlight = (rect: [number, number, number, number]) => {
    if (!doc) return;
    const ops: Operation[] = [];
    if (!doc.layers.available.includes(COMPLETED_LAYER)) ops.push({ type: "addLayer", layer: COMPLETED_LAYER });
    ops.push({ type: "addMarkup", markup: highlightMarkup(pageId, COMPLETED_LAYER, rect, inkColor === "red" ? "green" : inkColor) });
    apply(ops.length === 1 ? ops[0]! : { type: "batch", ops });
    if (!layers.includes(COMPLETED_LAYER)) setVisible([...layers, COMPLETED_LAYER]);
  };
  const placeSymbol = (p: Vec2) => {
    const size = Math.max(8, 36 / (viewport?.scale ?? 1));
    const m = symbolMarkup(pageId, layer, SYMBOL_SHAPES.some((x) => x.id === symbolShape) ? symbolShape : "triangle", p, size, inkColor, lineWidth);
    apply({ type: "addMarkup", markup: m });
  };
  const finishInk = () => {
    const stroke = inkRef.current; inkRef.current = null; setLiveInk(null);
    if (!stroke || stroke.pts.length < 2) return;
    const m = inkMarkup(pageId, layer, [stroke], inkColor, tool === "marker" ? "marker" : "pen");
    if (m) apply({ type: "addMarkup", markup: m });
  };
  const placeDetailBox = (rect: [number, number, number, number]) => {
    const d = detailing;
    if (!d || !doc || !projectId) return;
    const src = doc.plan.pages[d.srcIndex];
    if (!src) return;
    const targetIndex = doc.plan.pages.findIndex((p) => p.pageId === d.targetPageId);
    const title = d.label || `Detail on ${sheetLabelText(doc, targetIndex)}`;
    setDetailing(null); setTool("select");
    restoreRef.current = null;
    setPageIndex(d.srcIndex);
    void api.createPin(projectId, { documentId: id, pageId: src.pageId, x: d.x, y: d.y, type: "Detail", title, detail: { pageId: d.targetPageId, rect, ...(d.label ? { label: d.label } : {}) } })
      .then((r) => { pins.upsert(r.pin); setSelectedPinId(r.pin.id); })
      .catch((e) => setModal({ title: "Couldn't add the Detail pin", message: (e as Error).message, fields: [], submit: "OK", onSubmit: () => {} }));
  };
  const startDetail = (p: Vec2) => {
    if (!doc) return;
    setTool("select");
    const srcIndex = pageIndex;
    const others = doc.plan.pages.map((pg, i) => ({ value: pg.pageId, label: `${sheetLabelText(doc, i)}${doc.sheetLabels[pg.pageId] ? ` (sheet ${i + 1})` : ""}` }));
    setModal({
      title: "Detail pin",
      message: <>Pick the sheet the detail is on. Next you'll drag a box around the detail on that sheet.</>,
      fields: [
        { name: "target", label: "Detail is on sheet", type: "select", options: others, value: others[srcIndex + 1]?.value ?? others[0]!.value },
        { name: "label", label: "Label (optional)", placeholder: "e.g. 5/C4.12" },
      ],
      submit: "Next: draw the box",
      onSubmit: (v) => {
        const targetPageId = String(v.target);
        const ti = doc.plan.pages.findIndex((q) => q.pageId === targetPageId);
        if (ti < 0) return "Pick a sheet.";
        setDetailing({ srcIndex, x: p[0], y: p[1], targetPageId, label: String(v.label ?? "").trim().slice(0, 60) });
        setSelectedPinId(null);
        goTo(ti);
        setFit("page");
        setToolState("detailbox");
      },
    });
  };
  const dropPin = (p: Vec2) => {
    if (!projectId) return;
    if (tool === "detailpin") { startDetail(p); return; }
    if (tool === "photopin" || tool === "punchpin") { setTool("select"); setPhotoAt({ pageId, x: p[0], y: p[1], punch: tool === "punchpin" }); return; }
    if (tool === "subpin") { setTool("select"); setAttachAt({ pageId, x: p[0], y: p[1] }); return; }
    if (placingSub && projectId) { const sid = placingSub; setPlacingSub(null); setTool("select"); void api.placeSubmittal(projectId, sid, { documentId: id, pageId, x: p[0], y: p[1] }).then((r) => { pins.upsert(r.pin); setSelectedPinId(r.pin.id); }).catch((e) => setModal({ title: "Couldn't place the submittal", message: (e as Error).message, fields: [], submit: "OK", onSubmit: () => {} })); return; }
    if (movingPin) { const pin = pins.pins.find((x) => x.id === movingPin); setMovingPin(null); setTool("select"); if (pin) void api.updatePin(pin.id, { x: p[0], y: p[1], pageId, version: pin.version }).then((r) => pins.upsert(r.pin)); return; }
    api.createPin(projectId, { documentId: id, pageId, x: p[0], y: p[1], type: "Note", title: "" }).then((r) => { pins.upsert(r.pin); setSelectedPinId(r.pin.id); setTool("select"); }).catch((e) => setModal({ title: "Couldn't drop the pin", message: (e as Error).message, fields: [], submit: "OK", onSubmit: () => {} }));
  };

  /** Snap to the first point (closes a polygon), the draft, and vertices of takeoffs/clouds on this sheet. */
  const snapPoint = (p: Vec2): { p: Vec2; snapped: boolean } => {
    if (!doc || !viewport) return { p, snapped: false };
    const r = 14 / viewport.scale;
    const cands: Vec2[] = [...draft, ...doc.measurements.filter((m) => m.pageId === pageId).flatMap((m) => m.points), ...doc.markups.filter((m) => m.pageId === pageId && m.vertices).flatMap((m) => m.vertices!)];
    let best: Vec2 | null = null, bd = r;
    for (const c of cands) { const d = dist(c, p); if (d < bd) { bd = d; best = c; } }
    return best ? { p: best, snapped: true } : { p, snapped: false };
  };
  const drawsWith = (type: string) => type === "pen" || type === "mouse" || (type === "touch" && !pencilOnly);
  const isDrawTool = tool !== "select" && tool !== "pan";

  // Pointer events can arrive faster than React re-renders; keep the live drag box in a ref.
  const boxRef = useRef<{ start: Vec2; now: Vec2 } | null>(null);
  const setBox = (v: { start: Vec2; now: Vec2 } | null) => { boxRef.current = v; setBoxDrag(v); };
  const onPointerDown = (e: ReactPointerEvent) => {
    lastPointerType.current = e.pointerType;
    if (e.pointerType === "mouse") return mouseDown(e);
    if (e.pointerType === "touch" && Date.now() < penUntil.current) return; // palm rejection while the Pencil is in use
    if (e.pointerType === "pen") penUntil.current = Date.now() + 1000;
    try { (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId); } catch { /* synthetic or already-released pointer */ }
    pointers.current.set(e.pointerId, { type: e.pointerType, x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, t: Date.now() });
    const touches = [...pointers.current.values()].filter((q) => q.type === "touch");
    if (touches.length === 2) { // pinch: cancel any press
      press.current = null; setLoupe(null); inkRef.current = null; setLiveInk(null);
      const [a, b] = touches as [typeof touches[0], typeof touches[0]];
      const el = scroller.current!;
      const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
      const g = beginZoomGesture(cx, cy); // CSS-scaled preview while fingers move; one real render after they lift
      pinch.current = { d0: Math.hypot(a.x - b.x, a.y - b.y), z0: g?.target ?? zoom, cx, cy, left: el.scrollLeft, top: el.scrollTop };
      return;
    }
    const p = pagePoint(e);
    if (!p || !doc) return;
    if (!isDrawTool || !drawsWith(e.pointerType)) {
      if (tool === "select" && e.pointerType === "pen") { const hit = hitTest(doc, pageId, p, tol * 2.5, layers); setSelection(hit); if (hit) { setMoveDrag({ start: p, now: p }); return; } }
      const el = scroller.current!; pan.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
      if (tool === "select" && e.pointerType === "touch") press.current = { id: e.pointerId, start: p, type: "tap-select" };
      return;
    }
    e.preventDefault();
    if (FREEHAND.has(tool)) { inkRef.current = { pts: [p], pressure: [e.pointerType === "pen" ? e.pressure || 0.5 : 0.5] }; setLiveInk(inkRef.current); press.current = { id: e.pointerId, start: p, type: "ink" }; return; }
    press.current = { id: e.pointerId, start: p, type: "place" };
    if (tool === "highlight" || tool === "text" || tool === "stamp" || tool === "detailbox") setBox({ start: p, now: p });
    const sp = MULTI_POINT.has(tool) || tool === "calibrate" ? snapPoint(p) : { p, snapped: false };
    const v = toView(sp.p);
    setLoupe({ x: e.clientX, y: e.clientY, vx: v[0], vy: v[1], snapped: sp.snapped });
    setHover(sp.p);
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    if (e.pointerType === "mouse") return mouseMove(e);
    if (e.pointerType === "touch" && Date.now() < penUntil.current && !pointers.current.has(e.pointerId)) return;
    if (e.pointerType === "pen") penUntil.current = Date.now() + 1000;
    const rec = pointers.current.get(e.pointerId);
    if (rec) { rec.x = e.clientX; rec.y = e.clientY; }
    const el = scroller.current!;
    if (pinch.current) {
      const t = [...pointers.current.values()].filter((q) => q.type === "touch");
      if (t.length < 2) return;
      const [a, b] = t as [typeof t[0], typeof t[0]];
      const d = Math.hypot(a.x - b.x, a.y - b.y), cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
      const g = wheelZoom.current;
      el.scrollLeft -= cx - pinch.current.cx; el.scrollTop -= cy - pinch.current.cy; pinch.current.cx = cx; pinch.current.cy = cy;
      if (g && !g.done) {
        g.target = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinch.current.z0 * (d / Math.max(1, pinch.current.d0))));
        g.cx = cx; g.cy = cy; // the anchored page point travelled with the scroll, so it is under the new midpoint
        if (!wheelRaf.current) wheelRaf.current = requestAnimationFrame(() => { wheelRaf.current = 0; applyWheelPreview(); });
      }
      return;
    }
    if (pan.current && (!press.current || press.current.type === "tap-select")) {
      el.scrollLeft = pan.current.left - (e.clientX - pan.current.x);
      el.scrollTop = pan.current.top - (e.clientY - pan.current.y);
      return;
    }
    if (moveDrag) { const p = pagePoint(e); if (p) setMoveDrag({ ...moveDrag, now: p }); return; }
    if (!press.current || press.current.id !== e.pointerId) return;
    // Smooth ink: every coalesced sample the browser collected since the last frame.
    const samples = (e.nativeEvent as PointerEvent).getCoalescedEvents?.() ?? [];
    const events = samples.length ? samples : [e.nativeEvent as PointerEvent];
    if (press.current.type === "ink" && inkRef.current) {
      const pts = [...inkRef.current.pts], pr = [...inkRef.current.pressure];
      for (const ce of events) { const q = pagePoint(ce); if (q) { pts.push(q); pr.push(e.pointerType === "pen" ? ce.pressure || 0.5 : 0.5); } }
      inkRef.current = { pts, pressure: pr }; setLiveInk(inkRef.current);
      return;
    }
    const p = pagePoint(e);
    if (!p) return;
    if (boxRef.current) setBox({ ...boxRef.current, now: p });
    const sp = MULTI_POINT.has(tool) || tool === "calibrate" ? snapPoint(p) : { p, snapped: false };
    const v = toView(sp.p);
    setLoupe({ x: e.clientX, y: e.clientY, vx: v[0], vy: v[1], snapped: sp.snapped });
    setHover(sp.p);
  };

  const onPointerUp = (e: ReactPointerEvent) => {
    if (e.pointerType === "mouse") return mouseUp();
    const rec = pointers.current.get(e.pointerId);
    pointers.current.delete(e.pointerId);
    if (pinch.current) {
      if ([...pointers.current.values()].filter((q) => q.type === "touch").length < 2) {
        pinch.current = null;
        const g = wheelZoom.current;
        if (g && !g.done) { window.clearTimeout(g.timer); g.timer = window.setTimeout(commitWheelZoom, 150); }
      }
      pan.current = null; return;
    }
    if (moveDrag) { mouseUp(); return; }
    const pr = press.current;
    pan.current = null;
    if (!pr || pr.id !== e.pointerId) return;
    press.current = null;
    setLoupe(null);
    if (pr.type === "tap-select") {
      if (rec && Math.hypot(e.clientX - rec.sx, e.clientY - rec.sy) < 10 && doc) setSelection(hitTest(doc, pageId, pr.start, tol * 2.5, layers));
      return;
    }
    if (pr.type === "ink") { finishInk(); return; }
    const raw = pagePoint(e) ?? pr.start;
    const p = MULTI_POINT.has(tool) || tool === "calibrate" ? snapPoint(raw).p : raw;
    const bd = boxRef.current;
    if (bd) {
      const [x0, y0] = bd.start, [x1, y1] = p;
      const big = Math.abs(x1 - x0) > 8 / (viewport?.scale ?? 1) && Math.abs(y1 - y0) > 8 / (viewport?.scale ?? 1);
      const rect: [number, number, number, number] | null = big ? [Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0)] : null;
      setBox(null);
      if (tool === "text") placeText(rect, bd.start);
      else if (tool === "stamp") placeStamp(rect, bd.start);
      else if (tool === "highlight" && rect) placeHighlight(rect);
      else if (tool === "detailbox" && rect) placeDetailBox(rect);
      return;
    }
    if (tool === "pin" || tool === "photopin" || tool === "punchpin" || tool === "subpin" || tool === "detailpin") { dropPin(p); return; }
    if (tool === "symbol") { placeSymbol(p); return; }
    if (tool === "calibrate") { if (draft.length === 0) setDraft([p]); else { setDraft([]); openCalibrate(draft[0]!, p); } return; }
    if (!MULTI_POINT.has(tool)) return;
    // Double-tap (finger or Pencil) finishes; tapping the first point closes an area/cloud.
    const now = Date.now(), lt = lastTap.current;
    lastTap.current = { t: now, x: e.clientX, y: e.clientY };
    if (lt && now - lt.t < 350 && Math.hypot(e.clientX - lt.x, e.clientY - lt.y) < 24 && draft.length >= minPoints(tool) && tool !== "count") { lastTap.current = null; finishDraft(draft); return; }
    if (CLOSED.has(tool) && draft.length >= 3 && dist(p, draft[0]!) < 1e-6) { finishDraft(draft); return; }
    const next = [...draft, p];
    if ((tool === "arrow" || tool === "line") && next.length === 2) { finishDraft(next); return; }
    setDraft(next);
  };
  const onPointerCancel = (e: ReactPointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (press.current?.id === e.pointerId) { press.current = null; setLoupe(null); setBox(null); inkRef.current = null; setLiveInk(null); }
    pinch.current = null; pan.current = null;
  };
  // ---- Wheel / mouse navigation (desktop) ----
  // Wheel rules (docs/viewer-input.md):
  //  * ctrl/cmd + wheel (also a trackpad pinch) -> zoom at the cursor.
  //  * shift + wheel, or a meaningful horizontal component (thumb wheel, sideways swipe) -> pan.
  //  * vertical-only wheel -> zoom at the cursor (Bluebeam-style), proportional to deltaY so smooth-scroll
  //    streams of small/fractional deltas zoom smoothly. "Scroll wheel: Scroll" setting makes it pan instead.
  const wheelZoom = useRef<{ target: number; u: number; v: number; cx: number; cy: number; committed?: number; done?: boolean; timer?: number } | null>(null);
  const wheelRaf = useRef(0);
  const zoomRef = useRef(zoom); zoomRef.current = zoom;
  const viewportRef = useRef(viewport); viewportRef.current = viewport;
  const applyWheelPreview = () => {
    const g = wheelZoom.current, cv = canvasRef.current, vp = viewportRef.current;
    if (!g || !cv || !vp) return;
    const wrap = cv.parentElement as HTMLElement, k = g.target / vp.scale;
    if (Math.abs(k - 1) < 1e-4) { wrap.style.transform = ""; return; }
    wrap.style.transformOrigin = `${g.u * vp.width}px ${g.v * vp.height}px`;
    wrap.style.transform = `scale(${k})`;
  };
  const wheelZoomBy = (factor: number, cx: number, cy: number) => {
    const cv = canvasRef.current, vp = viewportRef.current;
    if (!cv || !vp) return;
    let g = wheelZoom.current;
    if (g && !g.done && Math.hypot(cx - g.cx, cy - g.cy) > 24) { // cursor moved: settle the old gesture, start a new anchor
      window.clearTimeout(g.timer); commitWheelZoom(); g = wheelZoom.current;
    }
    g = beginZoomGesture(cx, cy);
    if (!g) return;
    g.target = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, g.target * factor));
    if (!wheelRaf.current) wheelRaf.current = requestAnimationFrame(() => { wheelRaf.current = 0; applyWheelPreview(); });
    window.clearTimeout(g.timer);
    g.timer = window.setTimeout(commitWheelZoom, 140);
  };
  /** Starts (or continues) a CSS-previewed zoom gesture anchored at screen point cx,cy. Shared by wheel and touch pinch. */
  function beginZoomGesture(cx: number, cy: number) {
    const cv = canvasRef.current, vp = viewportRef.current;
    if (!cv || !vp) return null;
    let g = wheelZoom.current;
    if (g && !g.done) { window.clearTimeout(g.timer); return g; }
    {
      const base = g?.target ?? vp.scale;
      const r = cv.getBoundingClientRect(); // includes any preview transform, so u/v are true page fractions
      const pending = g?.committed && Math.abs(vp.scale - g.committed) > 1e-6 ? g.committed : undefined; // previous zoom still rendering
      g = { target: base, u: (cx - r.left) / Math.max(1, r.width), v: (cy - r.top) / Math.max(1, r.height), cx, cy, committed: pending };
      wheelZoom.current = g;
    }
    return g;
  }
  const commitWheelZoom = () => {
    const g = wheelZoom.current;
    if (!g || g.done) return;
    g.done = true; g.committed = g.target;
    setFit(null);
    if (Math.abs(g.target - zoomRef.current) < 1e-6 && Math.abs(g.target - (viewportRef.current?.scale ?? 0)) < 1e-6) { applyWheelPreview(); wheelZoom.current = null; return; }
    setZoom(g.target); // one real render at the final zoom
  };
  const handleWheel = useRef<(e: WheelEvent) => void>(() => {});
  handleWheel.current = (e: WheelEvent) => {
    const el = scroller.current;
    if (!el || !viewportRef.current) return;
    const unit = e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? el.clientHeight : 1;
    const dx = e.deltaX * unit, dy = e.deltaY * unit;
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) { wheelZoomBy(Math.exp(-dy * 0.01), e.clientX, e.clientY); return; } // pinch / ctrl+wheel
    if (e.shiftKey) { el.scrollLeft += dx || dy; return; }
    const horiz = Math.abs(dx) >= 1 && Math.abs(dx) >= Math.abs(dy) * 0.5;
    if (horiz) { el.scrollLeft += dx; el.scrollTop += dy; return; }
    if (wheelModeRef.current === "scroll") { el.scrollTop += dy; el.scrollLeft += dx; return; }
    wheelZoomBy(Math.exp(-Math.max(-150, Math.min(150, dy)) * 0.0015), e.clientX, e.clientY); // ~1.16x per 100px notch, capped per event
  };
  // Attach (non-passive) to whatever element is currently the scroll area; re-checked every render so a remount can't leave it unbound.
  const wheelBound = useRef<HTMLElement | null>(null);
  const wheelFn = useRef((e: WheelEvent) => handleWheel.current(e));
  useEffect(() => {
    const el = scroller.current;
    if (el === wheelBound.current) return;
    wheelBound.current?.removeEventListener("wheel", wheelFn.current);
    el?.addEventListener("wheel", wheelFn.current, { passive: false });
    wheelBound.current = el;
  });
  useEffect(() => () => { wheelBound.current?.removeEventListener("wheel", wheelFn.current); wheelBound.current = null; }, []);
  useEffect(() => () => { cancelAnimationFrame(wheelRaf.current); window.clearTimeout(wheelZoom.current?.timer); }, []);

  // While dragging, draw the moved item from a preview document (nothing is sent until mouse-up).
  const shownDoc: CenterlineDocument | null = useMemo(() => {
    if (!doc || !moveDrag || !selection) return doc;
    const op: Operation = selection.kind === "markup"
      ? { type: "moveMarkup", id: selection.id, dx: moveDrag.now[0] - moveDrag.start[0], dy: moveDrag.now[1] - moveDrag.start[1] }
      : { type: "moveMeasurement", id: selection.id, dx: moveDrag.now[0] - moveDrag.start[0], dy: moveDrag.now[1] - moveDrag.start[1] };
    try { return applyOp(doc, op).doc; } catch { return doc; }
  }, [doc, moveDrag, selection]);

  const toView = (p: Vec2): [number, number] => (viewport ? (viewport.convertToViewportPoint(p[0], p[1]) as [number, number]) : [0, 0]);

  const draftReadout = useMemo(() => {
    const pts = hover && MULTI_POINT.has(tool) && tool !== "count" ? [...draft, hover] : draft;
    if (pts.length < 2 || !fpp) return pts.length >= 2 ? UNSCALED_TEXT : null;
    const P = pts.map(asPoint);
    if (tool === "measureArea" || tool === "area") return pts.length >= 3 ? `${formatArea(areaUnit, areaSquareFeet(P, fpp))} · perimeter ${formatLength(lengthUnit, lengthFeet(P, true, fpp))}` : formatLength(lengthUnit, lengthFeet(P, false, fpp));
    return formatLength(lengthUnit, lengthFeet(P, false, fpp));
  }, [draft, hover, tool, fpp, lengthUnit, areaUnit]);

  const error = sync.error ?? pdfError;
  if (error) return <div className="center"><div className="error">{error}</div><a href="#/">Back to projects</a></div>;
  if (!doc || !meta || !shownDoc) return <div className="center muted">Opening plan…</div>;

  const pageInfo = doc.plan.pages[pageIndex];
  const onPage = { markups: doc.markups.filter((m) => m.pageId === pageId).length, takeoffs: doc.measurements.filter((m) => m.pageId === pageId).length };
  const stampDefs = doc.stampLibrary;
  const selectedQuantity = selectedMeasurement ? quantityText(selectedMeasurement, doc.pageScales[selectedMeasurement.pageId]) : null;

  const acting = tool === "punchpin" || tool === "photopin" || !!pinNote || tool === "subpin" || !!placingSub || tool === "detailpin" || !!(detailing && tool === "detailbox" && doc) || !!(detailView && doc) || !!movingPin || (draft.length > 0 && MULTI_POINT.has(tool));
  return (
    <div className="viewer">
      <header className="toolbar">
        <a className="button ghost" href={href({ name: "project", id: meta.projectId })} title="Back to project">←</a>
        <button className="ghost" onClick={() => setSidebar((s) => !s)} title="Sheet index ( [ )">☰</button>
        <div className="doc-title" title={doc.name}>{doc.name}</div>
        <div className="group">
          <button onClick={() => goTo(pageIndex - 1)} disabled={pageIndex <= 0} title="Previous sheet (← / PageUp)">‹</button>
          <form onSubmit={(e) => { e.preventDefault(); goTo(Number(pageInput) - 1); (document.activeElement as HTMLElement)?.blur(); }}>
            <input id="page-jump" className="page-input" value={pageInput} onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ""))} aria-label="Page number" title="Jump to page (G)" />
          </form>
          <span className="muted">/ {pageCount}</span>
          <button onClick={() => goTo(pageIndex + 1)} disabled={pageIndex >= pageCount - 1} title="Next sheet (→ / PageDown)">›</button>
        </div>
        <div className="group">
          <button onClick={zoomOut} title="Zoom out (−)">−</button>
          <span className="zoom">{Math.round(zoom * 100)}%</span>
          <button onClick={zoomIn} title="Zoom in (+)">+</button>
          <button className={fit === "page" ? "on" : ""} onClick={() => setFit("page")} title="Fit page (0)">Fit</button>
          <button className={fit === "width" ? "on" : ""} onClick={() => setFit("width")} title="Fit width (W)">Width</button>
        </div>
        <div className="group">
          <button onClick={sync.undo} disabled={!sync.history.canUndo} title="Undo (⌘Z)" aria-label="Undo">↶</button>
          <button onClick={sync.redo} disabled={!sync.history.canRedo} title="Redo (⇧⌘Z)" aria-label="Redo">↷</button>
        </div>
        <a className="button ghost compare-link" href={href({ name: "compare", n: id, np: pageIndex + 1 })} title="Overlay / compare this sheet with another revision">Compare</a>
        <span className="spacer" />
        <PresenceDots people={pins.live.presence.filter((q) => q.documentId === id)} me={pins.live.me} />
        <SyncPill status={sync.status} live={sync.live} pending={sync.pending} seq={sync.seq} />
        <button className={`ghost ${panel === "layers" ? "on" : ""}`} onClick={() => setPanel((p) => (p === "layers" ? "none" : "layers"))} title="Layers">Layers</button>
        <button className={`ghost ${panel === "info" ? "on" : ""}`} onClick={() => setPanel((p) => (p === "info" ? "none" : "info"))} title="About this plan set">ⓘ</button>
      </header>
      <div className="toolstrip" role="toolbar" aria-label="Markup tools">
        {TOOLS.filter((t) => (t.id !== "subpin" && t.id !== "detailpin") || office).map((t) => (
          <button key={t.id} data-tool={t.id} className={tool === t.id ? "on" : ""} onClick={() => { setTool(t.id); if (t.id === "stamp") setPanel("stamps"); }} title={t.title}>{t.label}</button>
        ))}
        <span className="sep" />
        {(FREEHAND.has(tool) || tool === "cloud" || tool === "arrow" || tool === "highlight" || tool === "line" || tool === "symbol") && <span className="ink-colors" aria-label="Ink colour">{COLOR_CHOICES.map((c) => <button key={c} className={`swatch c-${c} ${inkColor === c ? "on" : ""}`} aria-label={`Ink ${c}`} onClick={() => setInkColor(c)} />)}</span>}
        {tool === "symbol" && <span className="symbol-choices" aria-label="Symbol shape">{SYMBOL_SHAPES.map((x) => <button key={x.id} data-symbol={x.id} className={symbolShape === x.id ? "on" : ""} title={x.name} aria-label={x.name} onClick={() => setSymbolShape(x.id)}>{x.label}</button>)}</span>}
        {(tool === "line" || tool === "symbol") && <select className="line-width" value={lineWidth} onChange={(e) => setLineWidth(Number(e.target.value))} title="Line width" aria-label="Line width">{LINE_WIDTHS.map((w) => <option key={w} value={w}>{w} pt</option>)}</select>}
        <select className="wheel-mode" value={wheelMode} onChange={(e) => setWheelMode(e.target.value as "zoom" | "scroll")} title="What the mouse scroll wheel does on the plan" aria-label="Scroll wheel">
          <option value="zoom">Scroll wheel: Zoom</option><option value="scroll">Scroll wheel: Scroll</option>
        </select>
        <label className="toggle" title="When on, only the Apple Pencil (or mouse) draws; fingers always pan and pinch-zoom">
          <input type="checkbox" checked={pencilOnly} onChange={(e) => { setPencilOnly(e.target.checked); localStorage.setItem("cl.pencilOnly", e.target.checked ? "1" : "0"); }} /> Pencil only draws
        </label>
        <span className="pin-filter" aria-label="Show pin types">{PIN_TYPES.map((t) => <button key={t} className={`chip small ${pinFilter.has(t) ? "on" : ""}`} onClick={() => { const n = new Set(pinFilter); if (n.has(t)) n.delete(t); else n.add(t); setPinFilter(n); }}>{t}</button>)}</span>
        <button onClick={openScaleMenu} title="Set or remove this sheet's scale">Scale…</button>
        <select value={lengthUnit} onChange={(e) => setLengthUnit(e.target.value as QuickLengthUnit)} title="Quick-measure length unit">
          {QUICK_LENGTH_UNITS.map((u) => <option key={u} value={u}>{QUICK_LENGTH_UNIT_NAMES[u]}</option>)}
        </select>
        <select value={areaUnit} onChange={(e) => setAreaUnit(e.target.value as QuickAreaUnit)} title="Quick-measure area unit">
          {QUICK_AREA_UNITS.map((u) => <option key={u} value={u}>{QUICK_AREA_UNIT_NAMES[u]}</option>)}
        </select>
        <label className="layer-pick" title="New markups and takeoffs go on this layer">Layer
          <select value={layer} onChange={(e) => { setCurrentLayer(e.target.value); if (!layers.includes(e.target.value)) setVisible([...layers, e.target.value]); }}>
            {doc.layers.available.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
      </div>

      <div className="viewer-body">
        {sidebar && pdf && (
          <aside className="sidebar" aria-label="Sheet index">
            <div className="sidebar-head">Sheets <span className="muted">({pageCount})</span></div>
            {doc.plan.pages.map((p, i) => {
              const s = doc.pageScales[p.pageId];
              const label = doc.sheetLabels[p.pageId];
              const count = doc.markups.filter((m) => m.pageId === p.pageId).length + doc.measurements.filter((m) => m.pageId === p.pageId).length;
              return (
                <button key={p.pageId} ref={i === pageIndex ? activeThumb : undefined} className={`sheet ${i === pageIndex ? "active" : ""}`} onClick={() => goTo(i)} title={sheetLabelText(doc, i)}>
                  <Thumb pdf={pdf} index={i} documentId={id} />
                  <div className="sheet-meta">
                    <span className="sheet-no">{label?.number ?? `Sheet ${i + 1}`}</span>
                    {label?.title && <span className="sheet-title">{label.title}</span>}
                    <span className={`scale-badge ${s ? "" : "none"}`}>{s ? s.displayLabel : "No scale"}</span>
                    {count > 0 && <span className="markup-badge">{count} item{count === 1 ? "" : "s"}</span>}
                  </div>
                </button>
              );
            })}
          </aside>
        )}

        <div ref={scroller} className={`canvas-scroll tool-${tool}`} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel} onScroll={() => { if (sharp.current.canvas || baseOut.current < (window.devicePixelRatio || 1) * 0.98) scheduleSharp(); }}
          onPointerLeave={(e) => { if (e.pointerType === "mouse") { pan.current = null; setHover(null); } }} onClick={onClick} onDoubleClick={onDoubleClick} onContextMenu={onContextMenu} tabIndex={0}
          style={{ touchAction: "none" }} data-pencil-only={pencilOnly ? "1" : "0"}>
          <div className="canvas-inner">
            <div className="page-wrap" data-zoom={viewport ? viewport.scale : undefined} data-page={pageIndex + 1} data-seq={sync.seq} data-markups={onPage.markups} data-takeoffs={onPage.takeoffs}
              style={viewport ? { width: viewport.width, height: viewport.height } : undefined}>
              <canvas ref={canvasRef} className="page-canvas" />
              <div ref={sharpLayer} className="sharp-layer" aria-hidden="true" />
              {viewport && <MarkupCanvas doc={shownDoc} pageId={pageId} viewport={viewport} visibleLayers={layers} sharp={markupSharp} />}
              {viewport && (
                <svg className="overlay" width={viewport.width} height={viewport.height}>
                  {selectedMarkup && (() => {
                    const m = shownDoc.markups.find((x) => x.id === selectedMarkup.id) ?? selectedMarkup;
                    const pts = markupCorners(m).map(toView);
                    return <polygon className="selection" points={pts.map((q) => q.join(",")).join(" ")} />;
                  })()}
                  {selectedMeasurement && (() => {
                    const m = shownDoc.measurements.find((x) => x.id === selectedMeasurement.id) ?? selectedMeasurement;
                    const pts = m.points.map(toView);
                    return <g>{pts.map((q, i) => <rect key={i} className="handle" x={q[0] - 4} y={q[1] - 4} width={8} height={8} />)}
                      <polyline className="selection" points={(m.kind === "area" ? [...pts, pts[0]!] : pts).map((q) => q.join(",")).join(" ")} /></g>;
                  })()}
                  {liveInk && liveInk.pts.length > 1 && <polyline className={`live-ink ${tool === "marker" ? "marker" : ""}`} points={liveInk.pts.map(toView).map((q) => q.join(",")).join(" ")} />}
                  {(draft.length > 0 || (boxDrag && tool !== "select")) && (() => {
                    if (boxDrag) {
                      const a = toView(boxDrag.start), b = toView(boxDrag.now);
                      return <rect className="draft-box" x={Math.min(a[0], b[0])} y={Math.min(a[1], b[1])} width={Math.abs(b[0] - a[0])} height={Math.abs(b[1] - a[1])} />;
                    }
                    const live = hover && tool !== "count" ? [...draft, hover] : draft;
                    const pts = live.map(toView);
                    const closed = CLOSED.has(tool);
                    return (
                      <g>
                        {tool !== "count" && <polyline className={`draft-line ${closed ? "closed" : ""}`} points={(closed && pts.length > 2 ? [...pts, pts[0]!] : pts).map((q) => q.join(",")).join(" ")} />}
                        {pts.map((q, i) => <circle key={i} cx={q[0]} cy={q[1]} r={tool === "count" ? 6 : 3.5} className="draft-dot" />)}
                        {draftReadout && hover && <text x={toView(hover)[0] + 12} y={toView(hover)[1] - 12} className="check-label">{draftReadout}</text>}
                      </g>
                    );
                  })()}
                </svg>
              )}
              {viewport && <PinLayer pins={allPins.filter((p) => p.pageId === pageId)} toView={toView} selectedId={selectedPinId} filter={pinFilter} onSelect={(pid) => { const pp = allPins.find((x) => x.id === pid); if (pp?.type === "Detail" && pp.detail) { openDetail(pp); return; } if (pp?.type === "Photo" && (pp.version === 0 || pp.attachments.some((a) => a.contentType.startsWith("image/")))) { setPhotoOpen(pid); setSelection(null); return; } setSelectedPinId(pid); setSelection(null); }} />}
              {viewport && detailView && detailView.pin.detail && doc?.plan.pages[pageIndex]?.pageId === detailView.pin.detail.pageId && (() => {
                const [x, y, w, h] = detailView.pin.detail.rect;
                const a = toView([x, y]), b = toView([x + w, y + h]);
                return <div className="detail-focus" data-testid="detail-focus" style={{ left: Math.min(a[0], b[0]), top: Math.min(a[1], b[1]), width: Math.abs(b[0] - a[0]), height: Math.abs(b[1] - a[1]) }} />;
              })()}
            </div>
          </div>
          {rendering && <div className="rendering">Rendering…</div>}
          {loupe && canvasRef.current && <Loupe canvas={canvasRef.current} at={loupe} />}
        </div>

        {selectedPinId && pins.pins.find((p) => p.id === selectedPinId) && (office || pins.pins.find((p) => p.id === selectedPinId)!.type !== "Detail") && (
          <PinPanel pin={pins.pins.find((p) => p.id === selectedPinId)!} readOnly={!canEditPin(pins.pins.find((p) => p.id === selectedPinId)!)} onOpenDetail={openDetail} detailSheet={(() => { const dp = pins.pins.find((p) => p.id === selectedPinId)!.detail; const i = dp && doc ? doc.plan.pages.findIndex((q) => q.pageId === dp.pageId) : -1; return i >= 0 && doc ? sheetLabelText(doc, i) : undefined; })()} onClose={() => setSelectedPinId(null)} onChange={pins.upsert} onDelete={() => { pins.remove(selectedPinId); setSelectedPinId(null); }} onMove={() => { setMovingPin(selectedPinId); setTool("pin"); }} />
        )}

        {(selectedMarkup || selectedMeasurement) && panel !== "info" && (
          <aside className="inspector" aria-label="Selection">
            {selectedMarkup && <>
              <h4>{isLine(selectedMarkup) ? "Line" : selectedMarkup.kind === "shape" && !selectedMarkup.style.label.trim() ? "Symbol" : MARKUP_LABEL[selectedMarkup.kind]}</h4>
              <dl>
                <dt>Layer</dt><dd><select value={selectedMarkup.layer} onChange={(e) => apply({ type: "patchMarkup", id: selectedMarkup.id, set: { layer: e.target.value } })}>{doc.layers.available.map((l) => <option key={l}>{l}</option>)}</select></dd>
                <dt>Rotation</dt><dd><input className="num" value={String(Math.round(selectedMarkup.rotationDegrees * 10) / 10)} onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) apply({ type: "patchMarkup", id: selectedMarkup.id, set: { rotationDegrees: v } }); }} />°</dd>
                {selectedMarkup.kind === "note" && <><dt>Text</dt><dd className="clip">{selectedMarkup.style.text}</dd></>}
                {selectedMarkup.kind === "approvalStamp" && <><dt>Stamp</dt><dd>{selectedMarkup.style.title}</dd></>}
                {selectedMarkup.kind === "mot" && <><dt>Symbol</dt><dd>{selectedMarkup.style}</dd></>}
                {selectedMarkup.kind === "quickMeasure" && isLine(selectedMarkup) && <>
                  <dt>Width</dt><dd><select aria-label="Selected line width" value={selectedMarkup.style.lineWidth} onChange={(e) => apply({ type: "patchMarkup", id: selectedMarkup.id, set: { style: { ...selectedMarkup.style, lineWidth: Number(e.target.value) } } })}>{[...new Set([...LINE_WIDTHS, selectedMarkup.style.lineWidth!])].sort((a, b) => a - b).map((w) => <option key={w} value={w}>{w} pt</option>)}</select></dd>
                  <dt>Length</dt><dd data-testid="line-length">{doc.pageScales[selectedMarkup.pageId] ? formatLength(lengthUnit, lengthFeet((selectedMarkup.vertices ?? []).map(asPoint), false, doc.pageScales[selectedMarkup.pageId]!.feetPerPoint)) : "Sheet not scaled"}</dd>
                </>}
              </dl>
              {selectedMarkup.kind === "shape" && (() => {
                const sm = selectedMarkup, side = Math.round(Math.min(sm.bounds[2], sm.bounds[3]));
                const resize = (n: number) => { if (Number.isFinite(n) && n > 0) apply({ type: "patchMarkup", id: sm.id, set: { bounds: resizedAboutCentre(sm.bounds as [number, number, number, number], n) } }); };
                return <dl>
                  <dt>Shape</dt><dd>{sm.style.shape}</dd>
                  <dt>Size</dt><dd className="row"><button aria-label="Smaller" onClick={() => resize(side / 1.25)}>−</button><input className="num" aria-label="Symbol size" value={side} onChange={(e) => resize(Number(e.target.value))} /><button aria-label="Bigger" onClick={() => resize(side * 1.25)}>+</button></dd>
                  <dt>Width</dt><dd><select aria-label="Symbol line width" value={sm.style.lineWidth} onChange={(e) => apply({ type: "patchMarkup", id: sm.id, set: { style: { ...sm.style, lineWidth: Number(e.target.value) } } })}>{[...new Set([...LINE_WIDTHS, sm.style.lineWidth])].sort((a, b) => a - b).map((w) => <option key={w} value={w}>{w} pt</option>)}</select></dd>
                </dl>;
              })()}
              {recolor(selectedMarkup, "red") && <div className="swatches">{COLOR_CHOICES.map((c) => <button key={c} className={`swatch c-${c}`} title={c} aria-label={`Colour ${c}`} onClick={() => { const s = recolor(selectedMarkup, c); if (s) apply({ type: "patchMarkup", id: selectedMarkup.id, set: { style: s } }); }} />)}</div>}
              {authors[selectedMarkup.id] && <p className="muted small">By {authors[selectedMarkup.id]!.by} · {new Date(authors[selectedMarkup.id]!.at).toLocaleString()}</p>}
              <div className="row">{selectedMarkup.kind === "note" && <button onClick={editSelected}>Edit text…</button>}<button className="danger" onClick={deleteSelection}>Delete</button></div>
            </>}
            {selectedMeasurement && <>
              <h4>{selectedMeasurement.kind === "linear" ? "Linear takeoff" : selectedMeasurement.kind === "area" ? "Area takeoff" : selectedMeasurement.kind === "count" ? "Count" : "Arrow"}</h4>
              <button className="primary rename-takeoff" onClick={() => renameMeasurement(selectedMeasurement)} title="Rename this takeoff or change its pay item (double-click it with a mouse)">✎ Rename / pay item</button>
              <dl>
                <dt>Label</dt><dd className="clip" data-testid="takeoff-label">{selectedMeasurement.label || "—"}</dd>
                <dt>Quantity</dt><dd><strong>{selectedQuantity ?? (doc.pageScales[selectedMeasurement.pageId] ? "—" : "Sheet not scaled")}</strong></dd>
                <dt>Layer</dt><dd><select value={selectedMeasurement.layer} onChange={(e) => apply({ type: "patchMeasurement", id: selectedMeasurement.id, set: { layer: e.target.value } })}>{doc.layers.available.map((l) => <option key={l}>{l}</option>)}</select></dd>
                {selectedMeasurement.payItem && <><dt>Pay item</dt><dd>{selectedMeasurement.payItem.number} {selectedMeasurement.payItem.name}</dd></>}
              </dl>
              <div className="swatches">{COLOR_CHOICES.map((c) => <button key={c} className={`swatch c-${c}`} title={c} aria-label={`Colour ${c}`} onClick={() => apply({ type: "patchMeasurement", id: selectedMeasurement.id, set: { color: c } })} />)}</div>
              {authors[selectedMeasurement.id] && <p className="muted small">By {authors[selectedMeasurement.id]!.by} · {new Date(authors[selectedMeasurement.id]!.at).toLocaleString()}</p>}
              <div className="row"><button className="danger" onClick={deleteSelection}>Delete</button></div>
            </>}
          </aside>
        )}

        {panel === "stamps" && (
          <aside className="info-panel" aria-label="Stamps">
            <h3>Stamps</h3>
            <p className="muted small">Pick one, then click (or drag a box) on the sheet.</p>
            <div className="stamp-list">
              {STAMP_PRESETS.map((s) => (
                <button key={s.title} className={`stamp-choice c-text-${s.color} ${stamp.kind === "preset" && stamp.title === s.title ? "on" : ""}`} onClick={() => { setStamp({ kind: "preset", ...s }); setTool("stamp"); }}>{s.title}</button>
              ))}
            </div>
            <h4>Custom stamps <span className="muted small">(saved with this plan set)</span></h4>
            <div className="stamp-list">
              {stampDefs.length === 0 && <span className="muted small">None yet.</span>}
              {stampDefs.map((s) => (
                <div key={s.id} className="stamp-row">
                  <button className={`stamp-choice c-text-${s.details.style.textColor} ${stamp.kind === "custom" && stamp.stamp.id === s.id ? "on" : ""}`} onClick={() => { setStamp({ kind: "custom", stamp: s }); setTool("stamp"); }}>{s.name}</button>
                  <button className="ghost small" title="Remove from the list (placed stamps stay)" onClick={() => apply({ type: "deleteStampDefinition", id: s.id })}>✕</button>
                </div>
              ))}
            </div>
            <button onClick={openNewStamp}>+ New custom stamp…</button>
          </aside>
        )}

        {panel === "layers" && (
          <aside className="info-panel" aria-label="Layers">
            <h3>Layers</h3>
            <p className="muted small">Show or hide for you only. New markups go on the layer picked in the tool strip.</p>
            {doc.layers.available.map((l) => (
              <label key={l} className="field check">
                <input type="checkbox" checked={layers.includes(l)} onChange={(e) => setVisible(e.target.checked ? [...layers, l] : layers.filter((x) => x !== l))} /> <span>{l}</span>
                <span className="muted small">{doc.markups.filter((m) => m.layer === l).length + doc.measurements.filter((m) => m.layer === l).length}</span>
              </label>
            ))}
            <div className="row"><button onClick={() => setVisible([...doc.layers.available])}>Show all</button><button onClick={() => setVisible(doc.layers.visible)}>As saved</button></div>
            <div className="row"><button onClick={() => setModal({ title: "New layer", fields: [{ name: "n", label: "Layer name", value: "" }], submit: "Add layer", onSubmit: (v) => { const n = String(v.n).trim(); if (!n || doc.layers.available.includes(n)) return; apply({ type: "addLayer", layer: n }); setVisible([...layers, n]); setCurrentLayer(n); } })}>+ New layer…</button>
              {!doc.layers.available.includes(COMPLETED_LAYER) && <button onClick={() => { apply({ type: "addLayer", layer: COMPLETED_LAYER }); setVisible([...layers, COMPLETED_LAYER]); }}>+ Completed work</button>}</div>
          </aside>
        )}

        {panel === "info" && (
          <aside className="info-panel">
            <h3>{doc.name}</h3>
            <dl>
              <dt>Type</dt><dd>{KIND_LABEL[meta.kind]}</dd>
              <dt>Original file</dt><dd>{doc.import?.originalFileName}</dd>
              <dt>Sheets</dt><dd>{pageCount} · {Object.keys(doc.pageScales).length} scaled</dd>
              <dt>Markups</dt><dd>{doc.markups.length} markups · {doc.measurements.length} takeoffs · {doc.stampLibrary.length} custom stamps</dd>
              {doc.import?.desktopSavedAt && <><dt>Saved in Centerline</dt><dd>{new Date(doc.import.desktopSavedAt).toLocaleString()}</dd></>}
              <dt>Version</dt><dd>edit #{sync.seq}</dd>
              <dt>You appear as</dt><dd><button className="linkish" onClick={() => setModal({ title: "Your name", fields: [{ name: "n", label: "Shown to others when you edit", value: actorName() }], submit: "Save", onSubmit: (v) => { setActorName(String(v.n)); } })}>{actorName()}</button></dd>
              <dt>Format</dt><dd>centerline.document v{doc.formatVersion}</dd>
            </dl>
            {doc.import?.warnings.length ? <><h4>Import notes</h4><ul>{doc.import.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul></> : null}
            <h4>Downloads</h4>
            <ul className="links">
              <li><a href={`/api/documents/${id}/original`}>Original upload</a></li>
              <li><a href={`/api/documents/${id}/plan.pdf`} target="_blank" rel="noreferrer">Plan PDF (clean)</a></li>
              {doc.plan.flattenedBlobHash && <li><a href={`/api/documents/${id}/flattened.pdf`}>Flattened PDF as uploaded</a></li>}
              <li><a href={`/api/documents/${id}/document.json`} target="_blank" rel="noreferrer">Format v1 JSON</a></li>
            </ul>
            <h4>Keys</h4>
            <p className="muted small">V select · H pan · K calibrate · M/A quick measure · L/T/C takeoffs · X text · S stamp · Enter finish · Esc cancel · ⌫ delete · ⌘Z / ⇧⌘Z undo/redo · ← → sheets · + − zoom · 0 fit · [ sheet list</p>
          </aside>
        )}
      </div>

      {/* Action prompts (Done / Cancel / Undo point …) live in this fixed strip under the plan, never on the drawing surface. */}
      <footer className={`statusbar${acting ? " acting" : ""}`} data-acting={acting ? "1" : "0"}>
        {acting ? <div className="action-strip" role="group" aria-label="Current action">
          {tool === "punchpin" && <div className="done-bar" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}><span>Tap the spot that needs fixing</span><button onClick={() => setTool("select")}>Cancel</button></div>}
          {tool === "photopin" && <div className="done-bar" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}><span>Tap the spot for the photo</span><button onClick={() => setTool("select")}>Cancel</button></div>}
          {pinNote && <div className="done-bar" role="status" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}><span>{pinNote}</span><button onClick={() => setPinNote("")}>OK</button></div>}
          {tool === "subpin" && <div className="done-bar" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}><span>Tap the structure for this submittal</span><button onClick={() => setTool("select")}>Cancel</button></div>}
          {placingSub && <div className="done-bar" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}><span>Tap the spot for this submittal</span><button onClick={() => { setPlacingSub(null); setTool("select"); }}>Cancel</button></div>}
          {tool === "detailpin" && <div className="done-bar" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}><span>Tap the detail callout on this sheet</span><button onClick={() => setTool("select")}>Cancel</button></div>}
          {detailing && tool === "detailbox" && doc && <div className="done-bar" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}><span>Drag a box around the detail on {sheetLabelText(doc, pageIndex)}</span><button onClick={() => { const d = detailing; setDetailing(null); setTool("select"); goTo(d.srcIndex); }}>Cancel</button></div>}
          {detailView && doc && <div className="done-bar detail-bar" role="toolbar" aria-label="Detail" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
            <button className="primary" onClick={() => closeDetail(false)} aria-label="Back to the sheet you came from">← Back to {sheetLabelText(doc, detailView.back.pageIndex)}</button>
            <span>{detailView.pin.detail?.label || detailView.pin.title}</span>
            {office && <button onClick={() => closeDetail(true)}>Edit pin</button>}
          </div>}
          {movingPin && <div className="done-bar" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}><span>Tap the new spot for this pin</span><button onClick={() => { setMovingPin(null); setTool("select"); }}>Cancel</button></div>}
          {(draft.length > 0 && MULTI_POINT.has(tool)) && (
            <div className="done-bar" role="toolbar" aria-label="Drawing" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
              <span>{draft.length} pt{draft.length === 1 ? "" : "s"}{draftReadout ? ` · ${draftReadout}` : ""}</span>
              <button onClick={() => setDraft(draft.slice(0, -1))}>Undo point</button>
              <button onClick={() => setDraft([])}>Cancel</button>
              <button className="primary" disabled={draft.length < minPoints(tool)} onClick={() => finishDraft(draft)}>Done</button>
            </div>
          )}
        </div> : <>
        <span className="sheet-status" title="Sheet">{sheetLabelText(doc, pageIndex)} <span className="muted">· {pageIndex + 1} of {pageCount}</span></span>
        {canRenameSheets && <button className="linkish rename-sheet" onClick={renameSheet} title="Rename this sheet (number and title)" aria-label="Rename sheet">✎ Rename</button>}
        <button className={`scale-status linkish ${scale ? "ok" : "none"}`} onClick={openScaleMenu} title={scale ? `feetPerPoint ${scale.feetPerPoint}; 1 in on the sheet = ${feetPerInch(scale).toFixed(4)} ft` : "This sheet has no scale: click to set one, or use Calibrate."}>
          {statusScaleText(scale)}
        </button>
        {pageInfo && <span className="muted" title={`MediaBox ${pageInfo.mediaBox.join(", ")} · /Rotate ${pageInfo.rotate}`}>{paperSizeText(pageInfo)}{pageInfo.rotate ? ` · rotated ${pageInfo.rotate}°` : ""} · {Math.round(displaySize(pageInfo).width)}×{Math.round(displaySize(pageInfo).height)} pt</span>}
        <span className="muted">{onPage.markups} markups · {onPage.takeoffs} takeoffs on this sheet</span>
        <span className="cursor-status" title="Cursor in PDF page points (unrotated page space, same as Centerline)">
          {hover ? <>x {fmt1.format(hover[0])} · y {fmt1.format(hover[1])} pt</> : <span className="muted">cursor —</span>}
        </span>
        <span className="check-status">
          {draftReadout ? <strong>{draftReadout}</strong> : tool === "calibrate" ? <span className="muted">{draft.length ? "Click the other end of the known distance" : "Click one end of a known distance"}</span>
            : MULTI_POINT.has(tool) ? <span className="muted">Click points · double-click or Enter to finish · Esc cancels</span>
            : tool === "text" || tool === "stamp" ? <span className="muted">Click or drag a box on the sheet</span> : null}
        </span>
        <span className="spacer" />
        </>}
      </footer>

      <div className="notices" aria-live="polite">
        {sync.notices.map((n) => <div key={n.id} className={`notice ${n.kind}`}>{n.text}</div>)}
      </div>
      {photoAt && projectId && <PhotoPinSheet projectId={projectId} documentId={id} {...photoAt} me={me} onCancel={() => setPhotoAt(null)} onSaved={(r) => { setPhotoAt(null); if (r.pin) pins.upsert(r.pin); setPinNote(photoAt.punch ? (r.queued ? "Punch item saved on this device. It will sync when you have signal." : "Punch item added.") : r.queued ? "Photo pin saved on this device. It will sync when you have signal." : "Photo pin saved. It's in today's daily report."); window.setTimeout(() => setPinNote(""), 5000); }} />}
      {photoOpen && (() => {
        const q = pending.items.find((x) => x.id === photoOpen);
        const pin = pins.pins.find((x) => x.id === photoOpen);
        const pi = doc ? doc.plan.pages.findIndex((x) => x.pageId === (pin?.pageId ?? q?.pageId)) : -1;
        const sheet = pi >= 0 && doc ? sheetNumberText(doc, pi) || String(pi + 1) : undefined;
        if (q) return <PhotoView title={q.title} sheet={sheet} srcs={q.urls} pending canEdit onDelete={async () => { await dropQueued(q.id); window.dispatchEvent(new Event("cl:queued")); setPhotoOpen(null); }} onClose={() => setPhotoOpen(null)} />;
        if (!pin) return null;
        return <PhotoView title={pin.title} sheet={sheet} by={pin.createdBy} srcs={pin.attachments.filter((a) => a.contentType.startsWith("image/")).map((a) => api.pinFileUrl(pin.id, a.id))} canEdit={canEditPin(pin)}
          onDetails={() => { setPhotoOpen(null); setSelectedPinId(pin.id); }}
          onDelete={async () => { try { await api.deletePin(pin.id); pins.remove(pin.id); setPhotoOpen(null); } catch (e) { alert((e as Error).message); } }} onClose={() => setPhotoOpen(null)} />;
      })()}
      {attachAt && projectId && <AttachSubmittal projectId={projectId} spot={{ documentId: id, ...attachAt }} onCancel={() => setAttachAt(null)} onDone={(pin) => { setAttachAt(null); pins.upsert(pin); setSelectedPinId(pin.id); }} />}
      {modal && <Modal spec={modal} onClose={() => setModal(null)} />}
    </div>
  );
}

function SyncPill({ status, live, pending, seq }: { status: string; live: boolean; pending: number; seq: number }) {
  const text = status === "offline" ? `Offline · ${pending} queued` : status === "error" ? "Sync error" : pending ? `Saving ${pending}…` : live ? "Live · saved" : "Saved";
  return <span className={`sync-pill ${status} ${live ? "live" : ""}`} data-testid="sync-status" data-seq={seq} title={`Server version ${seq}${live ? " · receiving live edits" : " · live updates reconnecting"}`}>{text}</span>;
}

/** 2.5× magnifier over the press point so the finger/Pencil doesn't hide what you're placing. */
function Loupe({ canvas, at }: { canvas: HTMLCanvasElement; at: { x: number; y: number; vx: number; vy: number; snapped: boolean } }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const g = c.getContext("2d"); if (!g) return;
    const S = 120, Z = 2.5, ratio = canvas.width / Math.max(1, canvas.clientWidth);
    const src = (S / Z) * ratio;
    g.clearRect(0, 0, S, S);
    g.drawImage(canvas, at.vx * ratio - src / 2, at.vy * ratio - src / 2, src, src, 0, 0, S, S);
    g.strokeStyle = at.snapped ? "#16a34a" : "#dc2626"; g.lineWidth = 1.5;
    g.beginPath(); g.moveTo(S / 2, S / 2 - 12); g.lineTo(S / 2, S / 2 + 12); g.moveTo(S / 2 - 12, S / 2); g.lineTo(S / 2 + 12, S / 2); g.stroke();
    if (at.snapped) { g.beginPath(); g.arc(S / 2, S / 2, 8, 0, Math.PI * 2); g.stroke(); }
  }, [canvas, at]);
  const top = at.y - 170 < 8 ? at.y + 40 : at.y - 170;
  return <canvas ref={ref} width={120} height={120} className={`loupe ${at.snapped ? "snapped" : ""}`} style={{ left: at.x - 60, top }} aria-hidden />;
}
